import * as React from "react";
import {WelcomeHeader} from "components/signup/shared/WelcomeHeader";
import {EmployeeOrSpouseBlock, EOS} from "components/signup/shared/EmployeeOrSpouseBlock";
import InvalidBubble from "components/signup/shared/InvalidBubble";
import {GDPR} from "components/signup/shared/GDPR";
import {action, computed, observable} from "mobx";
import {UIState} from "data/UIState";
import {EMPTY_LOCATION, ILocation} from "data/entities/ILocation";
import {observer} from "mobx-react";
import {Alert} from "../../data/Alert";
import {handleSpacePressed, POST_QUERY} from "utils";
import {API} from "api";
import NativeSelect from "components/widgets/NativeSelect";

const EMPTY_EXTRA = {
  label:'',
  value:'',
};

export default function Step1Basics(Props) {
return (
  <>
    <WelcomeHeader/>
    <Step1BasicsContent {...Props} />
  </>
);
}

/**
 * This step collects:
 *
 * - Employee or Spouse
 * - Location
 * - GDPR
 * - The "extra" field if configured
 *
 */
@observer
class Step1BasicsContent extends React.Component<Props> {
  @observable employeeName = '';
  @observable private _employeeOrSpouse = EOS.employee;
  @observable extra = '';
  @observable location = EMPTY_LOCATION;

  @observable employeeOrSpouseInvalid = false;
  @observable locationInvalid = false;
  @observable gdprCheckFailed = false;
  @observable gdprAcceptanceStatus = !UIState.settings.ENABLE_GDPR;
  @observable employeeNameInvalid = false;
  @observable participationCode = "";
  @observable participationCodeFailed = false;

  @observable error = '';
  @observable selectedLocation:{id:string,name:string}|null = null;
  @observable selectedExtra:{id:string,name:string}|null = null;

  locationSelectRef: React.RefObject<HTMLSelectElement>;

  constructor(props:Props) {
    super(props);
    this.locationSelectRef = React.createRef<HTMLSelectElement>();
  }

  set employeeOrSpouse(employeeOrSpouse:string){
    this._employeeOrSpouse = employeeOrSpouse;
  }

  @computed get employeeOrSpouse() {
    return this._employeeOrSpouse;
  }

  @computed get locationOptions() {
    return this.props.locations.map(l=>({id:l._id, name:l.name}));
  }

  @action setLocation = (location:{id:string,name:string}) => {
    this.location = this.props.locations.find(l=>l._id===location.id) || EMPTY_LOCATION;
    this.selectedLocation = location;
  }

  @computed get extraOptions() {
    if(UIState.settings.EXTRA_FIELD.options) {
      return UIState.settings.EXTRA_FIELD.options.map(opt=>({id:opt.value, name:opt.label}));
    }
    return [];
  }

  @action setExtra = (extra:{id:string,name:string}) => {
    if(UIState.settings.EXTRA_FIELD.options) {
      const extraOpt = UIState.settings.EXTRA_FIELD.options.find(opt => opt.value === extra.id) || EMPTY_EXTRA;
      this.selectedExtra = extra;
      this.extra = extraOpt.label ; //extra && extra.label ? extra.label : "";
    }
    // <select
    //   name="extra"
    //   id="extra"
    //   onChange={action((e)=>{
    //     this.extra = e.target.value;
    //   })}
    //   value={this.extra}
    // >
    //   <option value={''}>Select {UIState.settings.EXTRA_FIELD.title}...</option>
    //   {UIState.settings.EXTRA_FIELD.options.map((opt, idx)=><option key={idx} value={opt.value}>{opt.label}</option>)}
    // </select>
  }


  @action
  handleGDPRAcceptanceStatusChange = (gdprAcceptanceStatus:boolean) => {
    this.gdprAcceptanceStatus = gdprAcceptanceStatus;
  }

  @action handleLocationChange = (e) => {
    if(e.target.value==='') {
      this.location = EMPTY_LOCATION;
    } else {
      const possibleLocation = this.props.locations.find(l=>l._id===e.target.value);
      this.location = possibleLocation ? possibleLocation : EMPTY_LOCATION;
    }
  }

  @action
  continue = () => {
    this.participationCodeFailed = false;
    if(UIState.settings.ENABLE_PARTICIPATION_CODE) {

      API.post('/checkParticipationCode', {code:this.participationCode})
        .then(action(res => {
          if(res.success) {
            this.validate();
          } else {
            this.participationCodeFailed = true;
            Alert.alert('Your participation code is incorrect. Please try again.');
          }}))
        .catch((err) => {
          Alert.alert('Oops, something went wrong. Please try again.');
        })
    } else {
      this.validate();
    }
  }

  @action
  validate = () => {

    // clear GDPR question
    this.gdprCheckFailed = false;

    // Handle Partner Spouse fields
    if(UIState.settings.ASK_FOR_EMPLOYEE_OR_SPOUSE) {
      this.employeeOrSpouseInvalid = false;
      //this.spouseNameInvalid = false;
      this.employeeNameInvalid = false;

      if(!this.employeeOrSpouse) {
        this.employeeOrSpouseInvalid = true;
      } else if(this.employeeOrSpouse===EOS.spouse) {
        if(UIState.settings.NAME_OF_EMPLOYEE_IF_SPOUSE && !this.employeeName) {
          this.employeeNameInvalid = true;
        }
      }
    }

    if(this.props.locations.length>0) {
      this.locationInvalid = this.location._id === '';
    } else {
      this.locationInvalid = false;
    }

    if(this.gdprAcceptanceStatus && !this.locationInvalid && !this.employeeNameInvalid && !this.employeeOrSpouseInvalid) {

      this.props.continue(
        {
          employeeOrSpouse:this.employeeOrSpouse,
          employeeName: this.employeeName,
          location: this.location,
          extra: this.extra,
        }
      );

    } else {
      this.gdprCheckFailed = true;

      const missingFields:string[] = [];
      this.employeeOrSpouseInvalid && missingFields.push('Association with '+UIState.settings.COMPANY_NAME);
      this.locationInvalid && missingFields.push(UIState.settings.LOCATION_LABEL);
      this.employeeNameInvalid && missingFields.push('Employee Name');
      !this.gdprAcceptanceStatus && missingFields.push('Consent and Acknowledgement of Your Privacy');

      let alertText ='';
      if(missingFields.length===1) {
        alertText = "You may not continue until you have completed the field "+missingFields[0];
      } else {
        alertText = "You may not continue until you have completed all required fields. These include: ";
        for(let i=0;i<missingFields.length;i++) {
          alertText += missingFields[i];
          if(i<missingFields.length-2) {
            alertText += ", ";
          } else if(i<missingFields.length-1) {
            alertText += (missingFields.length>2?",":"") + " and ";
          }
        }
      }

      Alert.alert(alertText);
    }
  }



  render() {


    return (
      <>



        {UIState.settings.ENABLE_PARTICIPATION_CODE && <>
          <div className="form-element">
            <label htmlFor={"participationCode"}>Enter participation code</label>
            <input
              type="text"
              id="participationCode"
              name="participationCode"
              value={this.participationCode}
              onChange={action((e: any) => {
                this.participationCode = e.target.value;
              })}
              placeholder={"participation code"}
              style={{minWidth: '250px'}}
            />
            <InvalidBubble label={`Participation code failed`} show={this.participationCodeFailed} />
          </div>
        </>}


        <EmployeeOrSpouseBlock
          employeeName={this.employeeName}
          updateEmployeeName={v => {
            this.employeeName = v;
          }}
          employeeOrSpouse={this.employeeOrSpouse}
          updateEmployeeOrSpouse={v => {
            this.employeeOrSpouse = v;
          }}
          employeeNameInvalid={this.employeeNameInvalid}
        />




        {this.props.locations.length>0 &&
          <div className={`form-element`}>
            <label htmlFor={'location-select'} id={`location-label`}>{UIState.settings.LOCATION_LABEL}</label>
            <NativeSelect
              id={'location-select'}
              options={this.locationOptions}
              selected={this.selectedLocation}
              emptyLabel={`Select ${UIState.settings.LOCATION_LABEL}...`}
              onChange={this.setLocation}
              labeledBy="location-label"
            />
            <InvalidBubble label={`${UIState.settings.LOCATION_LABEL} is required`} show={this.locationInvalid} />
          </div>
        }

        {UIState.settings.EXTRA_FIELD && UIState.settings.EXTRA_FIELD.enabled &&
          <div className="form-element">
            <label htmlFor={'extra'} id={`extra-label`}>{UIState.settings.EXTRA_FIELD.title} </label>
            <p className="hint">{UIState.settings.EXTRA_FIELD.hint} </p>

            {UIState.settings.EXTRA_FIELD.options ?
                <NativeSelect
                  id={'extra'}
                  options={this.extraOptions}
                  selected={this.selectedExtra}
                  emptyLabel={`Select ${UIState.settings.EXTRA_FIELD.title}...`}
                  onChange={this.setExtra}
                  labeledBy="extra-label"
                />
              :
              <textarea
                  id={"extra"}
                  value={this.extra}
                  onChange={action((e: any) => {
                    this.extra = e.target.value;
                  })}
                  style={{width: '80%', height: '60px', marginTop:'8px'}}
              />
            }
          </div>
        }


        {UIState.settings.ENABLE_GDPR &&
          <GDPR onStatusChange={this.handleGDPRAcceptanceStatusChange} checkFailed={this.gdprCheckFailed} />
        }

        <div className={'form-element tight'}>
          {/*<ButtonAction onClick={this.validateWhoAndTermsStep} className={"cta-button"}>Continue</ButtonAction>*/}
          <a tabIndex={0} className="cta-button noselect three-band-focus"
             onClick={this.continue}
             onKeyDown={(e)=>{ handleSpacePressed(e, this.continue);}}
          >Continue</a>
          {this.error && <p className="error">{this.error}</p>}
        </div>

      </>
    )
  }
}






export interface IBasicsData  {
  employeeName:string;
  employeeOrSpouse:string;
  location:any;
  extra:string;
}


interface Props {
  continue:(data:IBasicsData)=>void;
  locations:ILocation[]
}

